import React, { useState, useEffect, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CURRENCY_FULL_NAMES } from './_finHelpers';

const FALLBACK = {
  USD: 1, EUR: 0.876482, GBP: 0.744091, JPY: 156.5, CNY: 7.24, CAD: 1.36, AUD: 1.51,
  INR: 83.4, CHF: 0.91, HKD: 7.81, SGD: 1.34, NZD: 1.63, KRW: 1380, MXN: 17.1,
  BRL: 5.12, RUB: 92.3, ZAR: 18.5, SEK: 10.6, NOK: 10.5, TRY: 32.1, AED: 3.67,
  AFN: 71.5, ALL: 89.2, AMD: 388.4, ANG: 1.79, AOA: 850, ARS: 1050, AWG: 1.79
};

const CURRENCY_NAMES = CURRENCY_FULL_NAMES;

export default function CurrencyCalc() {
  const [amount, setAmount] = useState(100);
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('EUR');
  const [rates, setRates] = useState(FALLBACK);
  const [status, setStatus] = useState('loading');
  const [asOf, setAsOf] = useState('');
  const [base, setBase] = useState('USD'); // For All Currencies table base

  useEffect(() => {
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(d => {
        if (d && d.rates) {
          setRates({ ...FALLBACK, ...d.rates });
          setStatus('live');
          if (d.time_last_update_utc) setAsOf(d.time_last_update_utc);
        } else setStatus('fallback');
      })
      .catch(() => setStatus('fallback'));
  }, []);

  const symbols = useMemo(() => Object.keys(rates).sort(), [rates]);
  const converted = useMemo(() => {
    if (!rates[from] || !rates[to]) return 0;
    return (amount / rates[from]) * rates[to];
  }, [amount, from, to, rates]);
  const rateFT = useMemo(() => (rates[to] && rates[from]) ? (rates[to] / rates[from]) : 0, [rates, from, to]);

  const majors = ['USD','EUR','GBP','CNY','JPY','CAD','AUD'];
  const rateBetween = (a, b) => (rates[a] && rates[b]) ? (rates[b] / rates[a]) : 0;

  return (
    <>
      <CalcShell look="cnet" title="Currency Calculator"
        subtitle="Convert between the world's major currencies using current mid‑market rates."
        about={<>
          <p>The <b>Currency Calculator</b> converts amounts between global currencies using the latest available forex rates. Retail rates from banks or exchanges usually include a small spread over these mid‑market rates.</p>
        </>}
        slug="currency"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Amount</label>
              <input type="number" value={amount} onChange={e => setAmount(+e.target.value || 0)} data-testid="cur-amount"/>
            </div>
            <div className="field"><label>From</label>
              <select value={from} onChange={e => setFrom(e.target.value)} data-testid="cur-from">
                {symbols.map(s => <option key={s} value={s}>{s}{CURRENCY_NAMES[s]?` — ${CURRENCY_NAMES[s]}`:''}</option>)}
              </select>
            </div>
            <div className="field"><label>To</label>
              <select value={to} onChange={e => setTo(e.target.value)} data-testid="cur-to">
                {symbols.map(s => <option key={s} value={s}>{s}{CURRENCY_NAMES[s]?` — ${CURRENCY_NAMES[s]}`:''}</option>)}
              </select>
            </div>
            <div className="text-xs text-slate-500 mt-2">
              {status === 'live' ? <>Rates: live from openexchangerates {asOf && <>({asOf})</>}</> : status === 'fallback' ? 'Rates: static reference (live feed unavailable)' : 'Fetching latest rates…'}
            </div>
          </div>
          <div>
            <div className="result-panel">
              <div className="result-title">{amount.toLocaleString()} {from} =</div>
              <div className="result-value" data-testid="cur-result">
                {converted.toLocaleString(undefined, { maximumFractionDigits: 4 })} {to}
              </div>
              <div className="text-sm text-slate-600 mt-2">1 {from} = {rateFT.toFixed(6)} {to}</div>
              <div className="text-sm text-slate-600">1 {to} = {(rateFT ? 1/rateFT : 0).toFixed(6)} {from}</div>
            </div>
          </div>
        </div>

        <StepsPanel steps={[
          `Convert amount to USD: ${amount} ${from} ÷ ${rates[from]?.toFixed(6)} = ${(amount / (rates[from]||1)).toFixed(4)} USD`,
          `Convert USD to target: × ${rates[to]?.toFixed(6)} = <b>${converted.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${to}</b>`
        ]} />

      {/* Market Exchange Rates Table — moved INSIDE CalcShell so it stays in the tool area */}
      <div className="mt-6">
        <h3 className="font-bold text-slate-900 mb-2 text-lg">Market Exchange Rates Table</h3>
        <p className="text-sm text-slate-600 mb-4">
          The exchange rates below are based on the {asOf || 'latest available'} rates from openexchangerates.org.
        </p>

        <h4 className="font-semibold text-slate-900 mb-2">Major Currencies:</h4>
        <div className="overflow-auto mb-6">
          <table className="w-full text-sm border border-slate-200">
            <thead className="bg-slate-100">
              <tr><th className="px-2 py-1.5 border border-slate-200"></th>
                {majors.map(m => <th key={m} className="px-2 py-1.5 border border-slate-200">{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {majors.map(a => (
                <tr key={a}>
                  <td className="px-2 py-1.5 border border-slate-200 font-semibold bg-slate-50">1 {a}</td>
                  {majors.map(b => (
                    <td key={b} className="px-2 py-1.5 border border-slate-200 text-right">
                      {a === b ? '1' : rateBetween(a, b).toFixed(6)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4 className="font-semibold text-slate-900 mb-2">All Currencies:</h4>
        <div className="bg-slate-100 px-3 py-1.5 text-xs text-slate-700 flex items-center gap-2">
          <span>Base:</span>
          <select value={base} onChange={e=>setBase(e.target.value)} className="text-xs border rounded px-2 py-0.5" data-testid="cur-base">
            {symbols.map(s => <option key={s} value={s}>{s}{CURRENCY_NAMES[s]?` — ${CURRENCY_NAMES[s]}`:''}</option>)}
          </select>
        </div>
        <div className="overflow-auto max-h-96 border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-1.5 text-left border-b border-slate-200">Currency</th>
                <th className="px-2 py-1.5 text-right border-b border-slate-200">USD</th>
                <th className="px-2 py-1.5 text-right border-b border-slate-200">EUR</th>
                <th className="px-2 py-1.5 text-right border-b border-slate-200">GBP</th>
              </tr>
            </thead>
            <tbody>
              {symbols.map(sym => {
                const usd = rateBetween(base, 'USD');
                const eur = rateBetween(base, 'EUR');
                const gbp = rateBetween(base, 'GBP');
                if (sym !== base) return null;
                return (
                  <tr key={'base-'+sym} className="bg-blue-50 font-semibold">
                    <td className="px-2 py-1.5">1 {sym} — {CURRENCY_NAMES[sym] || sym}</td>
                    <td className="px-2 py-1.5 text-right">{usd.toFixed(6)}</td>
                    <td className="px-2 py-1.5 text-right">{eur.toFixed(6)}</td>
                    <td className="px-2 py-1.5 text-right">{gbp.toFixed(6)}</td>
                  </tr>
                );
              })}
              {symbols.filter(s => s !== base).map(sym => {
                const usd = rateBetween(base, sym) ? rateBetween(base, 'USD') : 0;
                const inv = rateBetween(sym, base);
                return (
                  <tr key={sym} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-1.5"><span className="text-blue-700">{sym}</span> — {CURRENCY_NAMES[sym] || sym}</td>
                    <td className="px-2 py-1.5 text-right">{rateBetween(sym, 'USD').toFixed(6)} <span className="text-slate-400 text-xs">({rateBetween('USD', sym).toFixed(6)})</span></td>
                    <td className="px-2 py-1.5 text-right">{rateBetween(sym, 'EUR').toFixed(6)} <span className="text-slate-400 text-xs">({rateBetween('EUR', sym).toFixed(6)})</span></td>
                    <td className="px-2 py-1.5 text-right">{rateBetween(sym, 'GBP').toFixed(6)} <span className="text-slate-400 text-xs">({rateBetween('GBP', sym).toFixed(6)})</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </CalcShell>
    </>
  );
}
